import React, { useCallback, useEffect, useState } from 'react';
import { Box, Drawer, Stack, TextField, Typography } from '@mui/material';
import toast from 'react-hot-toast';
import { useTheme } from '../../context/ThemeContext';
import { formatCurrency } from '../../config/currency';
import { PrimaryButton, SecondaryButton } from '../ui';
import {
  aggregatorApi,
  AggregatorSettlement,
  SuggestedAllocations,
} from '../../api/aggregatorApi';

interface SettlementEditDrawerProps {
  open: boolean;
  settlementId: number | null;
  onClose: () => void;
  onSaved?: () => void;
}

const SettlementEditDrawer: React.FC<SettlementEditDrawerProps> = ({
  open,
  settlementId,
  onClose,
  onSaved,
}) => {
  const { theme } = useTheme();
  const [settlement, setSettlement] = useState<AggregatorSettlement | null>(null);
  const [loading, setLoading] = useState(false);
  const [editPaymentDate, setEditPaymentDate] = useState('');
  const [editPeriodFrom, setEditPeriodFrom] = useState('');
  const [editPeriodTo, setEditPeriodTo] = useState('');
  const [editAmount, setEditAmount] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editSuggested, setEditSuggested] = useState<SuggestedAllocations | null>(null);
  const [editSubmitting, setEditSubmitting] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  const fetchSettlement = useCallback(async () => {
    if (!settlementId) return;
    setLoading(true);
    try {
      const res = await aggregatorApi.getSettlement(settlementId);
      const s = res.data;
      setSettlement(s);
      setEditPaymentDate(s.paymentDate);
      setEditPeriodFrom(s.periodFrom || '');
      setEditPeriodTo(s.periodTo || '');
      setEditAmount(String(s.paymentAmount));
      setEditNotes(s.notes || '');
      setEditSuggested(null);
      setEditError(null);
    } catch {
      toast.error('Failed to load settlement');
      onClose();
    } finally {
      setLoading(false);
    }
  }, [settlementId, onClose]);

  useEffect(() => {
    if (open && settlementId) fetchSettlement();
  }, [open, settlementId, fetchSettlement]);

  useEffect(() => {
    if (!open || !settlement) return;
    const amt = parseFloat(editAmount);
    if (Number.isNaN(amt) || amt <= 0) {
      setEditSuggested(null);
      return;
    }
    aggregatorApi
      .suggestAllocations({
        channelId: settlement.channelId,
        paymentAmount: amt,
        paymentDate: editPaymentDate,
        periodFrom: editPeriodFrom || undefined,
        periodTo: editPeriodTo || undefined,
        settlementId: settlement.id,
      })
      .then((res) => setEditSuggested(res.data))
      .catch(() => setEditSuggested(null));
  }, [open, editAmount, editPaymentDate, editPeriodFrom, editPeriodTo, settlement]);

  const submit = async () => {
    if (!settlement) return;
    const amt = parseFloat(editAmount);
    if (Number.isNaN(amt) || amt <= 0) {
      setEditError('Enter a valid amount');
      return;
    }
    setEditError(null);
    setEditSubmitting(true);
    try {
      await aggregatorApi.editSettlement(settlement.id, {
        paymentDate: editPaymentDate || undefined,
        periodFrom: editPeriodFrom || undefined,
        periodTo: editPeriodTo || undefined,
        paymentAmount: amt,
        notes: editNotes || undefined,
        allocations:
          editSuggested && editSuggested.suggested.length > 0
            ? editSuggested.suggested
                .filter((a) => a.allocatedAmount > 0)
                .map((a) => ({
                  entryType: a.entryType,
                  entryId: a.entryId,
                  allocatedAmount: a.allocatedAmount,
                }))
            : undefined,
      });
      toast.success('Settlement updated');
      onClose();
      if (onSaved) onSaved();
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string } } };
      setEditError(e?.response?.data?.message || 'Failed to update settlement');
    } finally {
      setEditSubmitting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={() => !editSubmitting && onClose()}
      PaperProps={{ sx: { width: { xs: '100%', sm: 480 }, p: 2 } }}
    >
      <Typography sx={{ fontSize: '1.05rem', fontWeight: 700, mb: 2 }}>Edit Settlement</Typography>
      {loading && <Typography variant="caption">Loading…</Typography>}
      {!loading && settlement && (
        <Stack spacing={2}>
          <TextField
            type="date"
            label="Payment date"
            value={editPaymentDate}
            onChange={(e) => setEditPaymentDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5 }}>
            <TextField
              type="date"
              label="Period from"
              value={editPeriodFrom}
              onChange={(e) => setEditPeriodFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Days of revenue this payout covers"
            />
            <TextField
              type="date"
              label="Period to"
              value={editPeriodTo}
              onChange={(e) => setEditPeriodTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <TextField
            type="number"
            label="Payment amount"
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            inputProps={{ min: 0, step: '0.01' }}
          />
          <TextField
            label="Notes (optional)"
            value={editNotes}
            onChange={(e) => setEditNotes(e.target.value)}
            multiline
            minRows={2}
          />

          {editSuggested && editSuggested.suggested.length > 0 && (
            <Box>
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                Re-suggested allocations
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                {editSuggested.suggested.map((a) => (
                  <Box
                    key={`${a.entryType}-${a.entryId}`}
                    sx={{ display: 'flex', justifyContent: 'space-between' }}
                  >
                    <Typography variant="caption" sx={{ color: theme.gray[500] }}>
                      {a.sourceLabel || `${a.entryType} #${a.entryId}`}
                      {a.entryDate ? ` · ${a.entryDate}` : ''}
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatCurrency(a.allocatedAmount)}
                    </Typography>
                  </Box>
                ))}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    mt: 0.5,
                    pt: 0.5,
                    borderTop: `1px solid ${theme.gray[200]}`,
                  }}
                >
                  <Typography variant="caption" sx={{ color: theme.gray[500] }}>
                    Cumulative gross · residual {formatCurrency(editSuggested.residual)}
                  </Typography>
                  <Typography variant="caption" sx={{ fontWeight: 600 }}>
                    {formatCurrency(editSuggested.cumulativeGross)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}

          {editError && (
            <Typography variant="caption" sx={{ color: theme.danger }}>
              {editError}
            </Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <SecondaryButton onClick={onClose} disabled={editSubmitting}>
              Cancel
            </SecondaryButton>
            <PrimaryButton onClick={submit} disabled={editSubmitting}>
              {editSubmitting ? 'Saving…' : 'Save Settlement'}
            </PrimaryButton>
          </Box>
        </Stack>
      )}
    </Drawer>
  );
};

export default SettlementEditDrawer;
